import {
  CardMedia,
  FormControl,
  FormHelperText,
  InputLabel,
  TextField,
  Theme,
} from '@mui/material';
import { DesktopDatePicker, LocalizationProvider, MobileDatePicker, PickersDay } from '@mui/lab';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import KeyboardArrowRightSharpIcon from '@mui/icons-material/KeyboardArrowRightSharp';
import { SxProps } from '@mui/system';
import { useField, useFormikContext } from 'formik';
import momentTimezone from 'moment-timezone';
import AdapterMoment from '@mui/lab/AdapterMoment';
import { isDateSame } from '../../utils/utilityFunctions';
import { useState } from 'react';
const defaultTextFieldStyles: SxProps<Theme> = {
  'label + &': {
    mt: 4,
  },
  '& .MuiInputBase-root': {
    border: '1px solid #5E9BC6',
  },
  '& .MuiOutlinedInput-root': {
    position: 'relative',
    backgroundColor: 'common.white',
    border: '1px solid #5E9BC6',
    fontSize: 16,
    '&:hover': {
      borderColor: 'primary.main',
      '.MuiOutlinedInput-notchedOutline': {
        border: 0,
      },
    },
    '.MuiOutlinedInput-notchedOutline': {
      border: 0,
    },
    '.MuiInputBase-input': {
      p: '10px 12px',
    },
    '&.Mui-focused, &:focus-visible': {
      outline: '3px solid #2057A6',
      outlineOffset: '2px',
      boxShadow: '0 0 0 3px rgba(32, 87, 166, 0.35)',
    },
  },
};

export const DatePicker = ({
  label = '',
  placeholder = '',
  name,
  inputLabelStyles,
  textFieldStyles = defaultTextFieldStyles,
  maxDate = new Date(),
  minDate,
  disabled = false,
  required = false,
  onClick,
}: {
  label?: string;
  placeholder: string;
  value?: string | null;
  onClick?: () => void;
  name: string;
  inputLabelStyles?: SxProps<Theme>;
  textFieldStyles?: SxProps<Theme>;
  maxDate?: Date;
  minDate?: Date;
  disabled?: boolean;
  required?: boolean;
}): JSX.Element => {
  const [field, meta, { setValue }] = useField(name);
  const { setFieldValue } = useFormikContext();
  const onChange = (date: Date | null) => {
    setFieldValue(name, moment(date).format());
    onClick && onClick();
  };
  const errorText = meta.error && meta.touched ? meta.error : '';
  const errorMessageId = `${name}-error`;
  const hasError = Boolean(errorText);
  const timeZoneFromServer = 'Asia/Kolkata';
  const { moment } = new AdapterMoment({ instance: momentTimezone });
  moment.tz.setDefault(timeZoneFromServer);
  momentTimezone.tz.setDefault(timeZoneFromServer);
  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <FormControl variant="outlined" fullWidth error={hasError} sx={{ my: { xs: 1, md: 2 } }}>
        <InputLabel shrink htmlFor="date-picker" sx={{ ...inputLabelStyles }}>
          {label}
        </InputLabel>
        <MobileDatePicker
          readOnly={false}
          shouldDisableDate={(date) => {
            if (maxDate && moment(maxDate).isBefore(moment(date))) {
              return true;
            } else if (minDate && isDateSame(minDate, date)) {
              return false;
            } else if (minDate && moment(minDate).isAfter(moment(date))) {
              return true;
            }
            return false;
          }}
          shouldDisableYear={(date) => {
            if (
              maxDate &&
              moment(new Date(maxDate).setFullYear(new Date(maxDate).getFullYear() + 1)).isBefore(
                moment(date)
              )
            ) {
              return true;
            } else if (minDate && isDateSame(minDate, date)) {
              return false;
            } else if (minDate && moment(minDate).isAfter(moment(date))) {
              return true;
            }
            return false;
          }}
          components={{ OpenPickerIcon: KeyboardArrowRightSharpIcon }}
          value={field.value}
          inputFormat="DD/MM/yyyy"
          onChange={onChange}
          disabled={disabled}
          views={['year', 'month', 'day']}
          DialogProps={{
            PaperProps: {
              'aria-label': 'Date of Birth/Incorporation',
              'aria-labelledby': undefined,
            },
          }}
          renderDay={(_day, _selectedDays, pickersDayProps) => (
            <PickersDay
              {...pickersDayProps}
              aria-current={pickersDayProps.selected ? 'true' : undefined}
            />
          )}
          renderInput={(params) => (
            <TextField
              fullWidth
              id="date-picker"
              {...params}
              required={required}
              error={hasError}
              InputProps={{ ...params.InputProps, readOnly: false }}
              inputProps={{
                ...params.inputProps,
                readOnly: false,
                'aria-readonly': undefined,
                'aria-label': undefined,
                autoComplete: 'off',
                ...(hasError ? { 'aria-invalid': true, 'aria-describedby': errorMessageId } : {}),
                role: 'button',
              }}
              placeholder={placeholder}
              sx={{
                ...textFieldStyles,
                '& .Mui-disabled': {
                  cursor: !disabled ? 'text' : 'not-allowed !important',
                  pointerEvents: 'all',
                },
              }}
            />
          )}
          disableCloseOnSelect={false}
          showToolbar={false}
        />
        {errorText && (
          <FormHelperText
            id={errorMessageId}
            error
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
            sx={{ marginLeft: 'unset' }}>
            {errorText}
          </FormHelperText>
        )}
      </FormControl>
    </LocalizationProvider>
  );
};

export const TextDatePicker = ({
  label = '',
  placeholder = '',
  name,
  inputLabelStyles,
  textFieldStyles = defaultTextFieldStyles,
  maxDate = new Date(),
  minDate,
  disabled = false,
  required = false,
  onClick,
}: {
  label?: string;
  placeholder: string;
  value?: string | null;
  onClick?: () => void;
  name: string;
  inputLabelStyles?: SxProps<Theme>;
  textFieldStyles?: SxProps<Theme>;
  maxDate?: Date;
  minDate?: Date;
  disabled?: boolean;
  required?: boolean;
}): JSX.Element => {
  const [field, meta, { setValue }] = useField(name);
  const [error, setError] = useState(false);
  const { moment } = new AdapterMoment({ instance: momentTimezone });

  const invalidDate = (date: any) => {
    !date
      ? setError(false)
      : date.toString().toLowerCase() === 'invalid date'
      ? setError(true)
      : setError(false);
  };

  const onChange = (date: any) => {
    invalidDate(date);
    const date1 = moment(new Date());
    const date2 = date
      ? moment(date).get('hours') === 0 &&
        moment(date).get('minutes') === 0 &&
        moment(date).get('seconds') === 0
        ? moment(date)
            .add(date1.get('hours'), 'hours')
            .add(date1.get('minutes'), 'minutes')
            .add(date1.get('seconds'), 'seconds')
            .format()
        : moment(date).format()
      : null;
    setValue(date2);
    invalidDate(date2);
    onClick && onClick();
  };
  const errorText = error ? 'Invalid Date' : meta.error && meta.touched ? meta.error : '';
  const errorMessageId = `${name}-error`;
  const hasError = Boolean(errorText);
  const timeZoneFromServer = 'Asia/Kolkata';
  moment.tz.setDefault(timeZoneFromServer);
  momentTimezone.tz.setDefault(timeZoneFromServer);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <FormControl variant="outlined" fullWidth error={hasError} sx={{ my: { xs: 1, md: 2 } }}>
        <InputLabel shrink htmlFor="date-picker" sx={{ ...inputLabelStyles }}>
          {label}
        </InputLabel>
        <DesktopDatePicker
          readOnly={false}
          shouldDisableDate={(date) => {
            if (maxDate && moment(maxDate).isBefore(moment(date))) {
              return true;
            } else if (minDate && moment(minDate).isAfter(moment(date))) {
              return true;
            }
            return false;
          }}
          shouldDisableYear={(date) => {
            if (
              maxDate &&
              moment(new Date(maxDate).setFullYear(new Date(maxDate).getFullYear() + 1)).isBefore(
                moment(date)
              )
            ) {
              return true;
            } else if (minDate && moment(minDate).isAfter(moment(date))) {
              return true;
            }
            return false;
          }}
          components={{
            OpenPickerIcon: () => <CardMedia component="img" src="/images/calender.svg" />,
          }}
          componentsProps={{}}
          value={field.value}
          inputFormat="DD/MM/yyyy"
          onChange={onChange}
          disabled={disabled}
          views={['year', 'month', 'day']}
          InputAdornmentProps={{ position: 'start' }}
          renderDay={(_day, _selectedDays, pickersDayProps) => (
            <PickersDay
              {...pickersDayProps}
              aria-current={pickersDayProps.selected ? 'true' : undefined}
            />
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              id="date-picker"
              placeholder={placeholder}
              required={required}
              error={hasError}
              InputProps={{ ...params.InputProps, readOnly: false }}
              inputProps={{
                ...params.inputProps,
                readOnly: false,
                'aria-readonly': undefined,
                'aria-label': undefined,
                autoComplete: 'off',
                ...(hasError ? { 'aria-invalid': true, 'aria-describedby': errorMessageId } : {}),
              }}
              sx={{
                ...textFieldStyles,
                display: 'flex',
                flexDirection: 'row-reverse',
                '& .Mui-disabled': {
                  cursor: !disabled ? 'text' : 'not-allowed !important',
                  pointerEvents: 'all',
                },
              }}
              disabled={disabled}
            />
          )}
          disableCloseOnSelect={false}
          showToolbar={false}
        />
        {errorText && (
          <FormHelperText
            id={errorMessageId}
            error
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
            sx={{ marginLeft: 'unset' }}>
            {errorText}
          </FormHelperText>
        )}
      </FormControl>
    </LocalizationProvider>
  );
};
